<!--
  SupportSLATimer.svelte — one SLA clock: how long is left, or that it was missed.

  A ticket runs two clocks (first response and resolution) and the agent view
  shows both; the queue shows only the one that matters next, in `compact` form.
  The phase comes from `supportSLAState`, which trusts the server's breach flag
  but also reads an overdue due time as breached, so a ticket does not look on
  track for the minutes until the next cron pass.
-->
<script lang="ts">
	import { onDestroy, onMount } from 'svelte';
	import { formatSupportDuration, supportSLAState, type SupportSLAInput } from '../support/supportFormat';
	import { t } from '../i18n';

	interface Props extends SupportSLAInput {
		/** What the clock measures, e.g. "First response". Omitted in compact form. */
		label?: string;
		/** A single line with no label, for table cells. */
		compact?: boolean;
	}

	let { label = '', compact = false, dueAt, isBreached, isPaused, completedAt }: Props = $props();

	/** The current time, ticking each minute so a countdown on an open page stays true. */
	let now = $state(Date.now());
	/** The minute timer. */
	let timer: ReturnType<typeof setInterval> | undefined;

	onMount(() => {
		timer = setInterval(() => (now = Date.now()), 60_000);
	});

	onDestroy(() => clearInterval(timer));

	/** Where the clock stands. */
	let sla = $derived(supportSLAState({ dueAt, isBreached, isPaused, completedAt }, now));

	/** The remaining (or overdue) time, formatted. */
	let remaining = $derived(
		sla.minutesRemaining === null ? '' : formatSupportDuration(sla.minutesRemaining)
	);

	/** Colour and icon per phase — only a breach and an imminent one are allowed to be loud. */
	const TONES: Record<string, { tone: string; icon: string }> = {
		none: { tone: 'text-gray-400 dark:text-gray-500', icon: 'fa-minus' },
		met: { tone: 'text-green-600 dark:text-green-400', icon: 'fa-circle-check' },
		paused: { tone: 'text-gray-500 dark:text-gray-400', icon: 'fa-circle-pause' },
		breached: { tone: 'text-red-600 dark:text-red-400', icon: 'fa-triangle-exclamation' },
		'due-soon': { tone: 'text-amber-600 dark:text-amber-400', icon: 'fa-hourglass-half' },
		'on-track': { tone: 'text-gray-700 dark:text-gray-300', icon: 'fa-clock' }
	};

	/** The sentence the clock reads as. */
	let text = $derived.by(() => {
		switch (sla.phase) {
			case 'met':
				return $t('Met');
			case 'paused':
				return $t('Paused · {{time}} left', { time: remaining });
			case 'breached':
				return sla.minutesRemaining === null
					? $t('Missed')
					: $t('Overdue by {{time}}', { time: remaining });
			case 'due-soon':
			case 'on-track':
				return $t('{{time}} left', { time: remaining });
			default:
				return $t('No target');
		}
	});
</script>

<div
	class="flex items-center gap-1.5 text-sm {TONES[sla.phase].tone}"
	data-sla-phase={sla.phase}
	title={dueAt ? new Date(dueAt).toLocaleString() : undefined}
>
	<i class="fa-solid {TONES[sla.phase].icon} text-xs"></i>
	{#if label && !compact}
		<span class="text-default">{label}:</span>
	{/if}
	<span class="font-medium {compact ? 'whitespace-nowrap' : ''}">{text}</span>
</div>
